import { useQuery } from "@tanstack/react-query";
import { Link, useRouterState } from "@tanstack/react-router";
import { IconCamera, IconHome } from "@wilco/ui";
import { useState, type CSSProperties } from "react";
import { bottomTiles, deskQuery } from "../desk/desk.ts";
import { formatNumber, t } from "../i18n.ts";
import { ScanDialog } from "./ScanDialog.tsx";
import { TargetLink, targetPath } from "./targets.tsx";

/**
 * Нижняя панель телефона: «Стіл», до трёх плиток роли, скан. Счётчик
 * серый, красный только у тревоги (тон danger): иначе «Комплекти 7»
 * выглядели бы аварией. Над открытым пунктом оранжевая черта, она
 * переезжает к нажатому сдвигом, без перекладки.
 */
export function BottomBar({ onScan }: { onScan: (code: string) => void }) {
  const { data: desk } = useQuery(deskQuery);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [scanOpen, setScanOpen] = useState(false);
  const tiles = desk ? bottomTiles(desk) : [];
  const count = tiles.length + 2;

  const activeIndex = pathname === "/" ? 0 : tiles.findIndex((tile) => pathname === targetPath(tile.target)) + 1 || -1;
  const indicator = {
    width: `${String(100 / count)}%`,
    transform: `translateX(${String(Math.max(activeIndex, 0) * 100)}%)`,
  } satisfies CSSProperties;

  return (
    <nav className="bottom" aria-label={t("shell.quickActions")}>
      <span className="bottom-indicator" data-on={activeIndex >= 0 ? "" : undefined} style={indicator} aria-hidden="true" />
      <Link to="/" className="bottom-item" activeOptions={{ exact: true }}>
        <IconHome className="bottom-icon" />
        <span className="bottom-label">{t("bottom.desk")}</span>
      </Link>
      {tiles.map((tile) => {
        const Icon = tile.icon;
        return (
          <TargetLink key={tile.key} dest={tile.target} className="bottom-item">
            <span className="bottom-icon-wrap">
              <Icon className="bottom-icon" />
              {tile.count ? (
                <span className="bottom-badge" data-tone={tile.tone === "danger" ? "danger" : undefined}>
                  {formatNumber(tile.count)}
                </span>
              ) : null}
            </span>
            {/* Короткая подпись плитки обязательна для панели (desk.ts, bottom). */}
            <span className="bottom-label">{tile.bottom ? t(tile.bottom) : t(tile.title)}</span>
          </TargetLink>
        );
      })}
      <button type="button" className="bottom-item bottom-scan" onClick={() => setScanOpen(true)}>
        <IconCamera className="bottom-icon" />
        <span className="bottom-label">{t("bottom.scan")}</span>
      </button>
      <ScanDialog open={scanOpen} onOpenChange={setScanOpen} onScan={onScan} />
    </nav>
  );
}
